import { parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
  ReactSingleSelectInput,
  SelectForm,
} from '@/components/uiParts';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import {
  MenuManagementSearchArea,
  SEARCH_AREA_ID,
} from '@/pages/menu-managements/components/SearchAndDrag/MenuManagementSearchArea';
import {
  convertStrToFormatDate,
  executionTableThs,
} from '@/pages/menu-managements/executions/service';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  ExecutionListResponse,
  useMenuManagementExecutionList,
} from '@/services/Execution/hooks';

const MenuManagementExecutionsPage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [Jobs, setJobs] = useState<ExecutionListResponse[]>([]);
  const JobLength = Jobs.length;
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [targetAssociateBrandSelect, setTargetAssociateBrandSelect] =
    useState<SelectForm | null>({
      label: '',
      value: '',
    });

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: targetAssociateBrandSelect,
    options: associateBrandSelectOptions,
    onChange: async (associateBrand: SelectForm | null) => {
      if (
        associateBrand === null ||
        associateBrand.value === targetAssociateBrandSelect?.value
      )
        return;
      const associateBrandId = associateBrand.value;
      setIsLoading(true);
      setTargetAssociateBrandSelect(associateBrand);
      const res = await useMenuManagementExecutionList({
        associate_brand_id: associateBrandId,
      });
      setJobs(res);
      setIsLoading(false);
    },
    onReset: () => {
      setTargetAssociateBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const associateBrands = await useAssociateBrandList();
      const associateBrandOptions = associateBrands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id.toString(),
        };
      });
      setAssociateBrandSelectOptions(associateBrandOptions);

      const { associateBrand } = LocalStorageHandler.get('menuManagement');
      const initialAssociateBrand =
        associateBrand !== undefined
          ? associateBrand
          : associateBrandOptions[0];
      LocalStorageHandler.set('menuManagement', {
        associateBrand: initialAssociateBrand,
      });
      const res = await useMenuManagementExecutionList({
        associate_brand_id: initialAssociateBrand.value,
      });
      setTargetAssociateBrandSelect(initialAssociateBrand);
      setJobs(res);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout
      title={routes.menuManagementExecutions.title}
      {...{ associateBrandSelectInput }}
    >
      <CmBox>
        <MenuManagementSearchArea
          targetInputLabel="カテゴリ"
          newPagePath={'/menu-managements/executions/new'}
          {...{ associateBrandSelectInput, searchCount: JobLength }}
        />
        {JobLength > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
          >
            <CmScrollBox>
              <CmBaseTable ths={executionTableThs}>
                {Jobs.map((job, index) => {
                  const { date, method } = (() => {
                    if (job.scheduled_date !== null) {
                      const date = DateDomainService.datetimeFormat(
                        new Date(convertStrToFormatDate(job.scheduled_date))
                      );
                      return { date, method: '日時指定' };
                    } else {
                      const date = DateDomainService.datetimeFormat(
                        parseISO(job.created_at)
                      );
                      return { date, method: '即時実行' };
                    }
                  })();
                  const executeStatus = job.is_executed ? '実行済' : '予約中';
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd textAlign="center">
                        <CmLink
                          color="blue.500"
                          to={`/menu-managements/executions/${job.id}`}
                        >
                          {date}
                        </CmLink>
                      </CmTd>
                      <CmTd textAlign="center">{method}</CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={executeStatus}>
                          {executeStatus}
                        </CmTooltip>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致するメニュー配信予約が見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </PageLayout>
  );
};

export default MenuManagementExecutionsPage;
